import type { Style } from '@dreamquiz/shared';
import type { TerminalView } from './terminal.js';

interface BootStep {
  text: string;
  style?: Style;
  /** Pause nach dieser Zeile in ms. */
  wait?: number;
}

const FULL_STEPS: BootStep[] = [
  { text: '$ sudo apt update && sudo apt upgrade -y', style: 'muted', wait: 320 },
  { text: 'Get:1 https://dream.repo/dreamquiz stable InRelease [4.212 B]', style: 'muted', wait: 150 },
  { text: 'Get:2 https://dream.repo/dreamquiz stable/main amd64 Packages [61,4 kB]', style: 'muted', wait: 180 },
  { text: 'Reading package lists... Done', style: 'muted', wait: 220 },
  { text: 'The following packages will be upgraded:', style: 'muted', wait: 120 },
  { text: '  dreamquiz-core dreamquiz-terminal quizmaster-ai unicorn-fx', style: 'muted', wait: 260 },
  { text: 'Unpacking dreamquiz-core (0.1.0) ...', style: 'muted', wait: 200 },
  { text: 'Setting up quizmaster-ai (0.1.0) ...', style: 'muted', wait: 200 },
  { text: 'Setting up unicorn-fx (1.0.0) ... 🦄', style: 'muted', wait: 240 },
];

const SHORT_STEPS: BootStep[] = [
  { text: '$ sudo apt update && sudo apt upgrade -y', style: 'muted', wait: 200 },
  { text: 'Reading package lists... Done', style: 'muted', wait: 160 },
  { text: 'dreamquiz-core is already the newest version (0.1.0).', style: 'muted', wait: 160 },
];

const BAR_WIDTH = 24;

/**
 * Fake-Update beim Einstieg (Konzept Kap. 7).
 * Erster Besuch bekommt die lange Fassung, danach die Kurzfassung.
 * Enter überspringt jederzeit.
 */
export async function runBootSequence(
  term: TerminalView,
  options: { full: boolean },
): Promise<void> {
  const steps = options.full ? FULL_STEPS : SHORT_STEPS;
  const skip = { done: false };

  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      skip.done = true;
    }
  };
  window.addEventListener('keydown', onKey);

  try {
    for (const step of steps) {
      term.local(step.text, step.style);
      if (skip.done) continue;
      await sleep(step.wait ?? 150);
    }

    if (!skip.done) await progressBar(term, skip, options.full ? 1100 : 450);
    else term.local(`[${'█'.repeat(BAR_WIDTH)}] 100%`, 'muted');

    term.local('[OK] DreamQuiz bereit.', 'ok');
  } finally {
    window.removeEventListener('keydown', onKey);
  }
}

async function progressBar(
  term: TerminalView,
  skip: { done: boolean },
  durationMs: number,
): Promise<void> {
  const line = term.local('', 'muted');
  const span = line.firstElementChild as HTMLElement;
  const startedAt = performance.now();

  return new Promise((resolve) => {
    const tick = () => {
      const ratio = skip.done ? 1 : Math.min(1, (performance.now() - startedAt) / durationMs);
      const filled = Math.round(ratio * BAR_WIDTH);
      span.textContent = `[${'█'.repeat(filled)}${'░'.repeat(BAR_WIDTH - filled)}] ${Math.round(ratio * 100)}%`;
      if (ratio >= 1) {
        resolve();
        return;
      }
      window.setTimeout(tick, 40);
    };
    tick();
  });
}

const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));
